/**
 * AuthCallbackPage — OAuth redirect target for Cognito SSO.
 *
 * Cognito redirects here (the client app's SSO_REDIRECT_URI, e.g.
 * https://.../auth/callback) with `?code=...` after the user authenticates with
 * the IdP. We exchange the code for a session via the auth store, then land the
 * user in the app. On error we bounce back to /login with a toast.
 */

import { clientConfig } from '@/config/clientConfig';
import { useToast } from '@/shared/hooks/useToast';
import { Card } from '@/shared/ui/card';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useAuthStore } from '../store/authStore';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const { completeSsoLogin } = useAuthStore();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // Guard against React 18 StrictMode double-invoke (the code is single-use).
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const code = searchParams.get('code');
    const oauthError = searchParams.get('error');
    const oauthErrorDesc = searchParams.get('error_description');

    if (oauthError) {
      const msg = oauthErrorDesc || 'Single sign-on was cancelled or failed.';
      setErrorMessage(msg);
      toast({ title: 'Sign-in failed', description: msg, variant: 'destructive' });
      const t = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(t);
    }

    if (!code) {
      const msg = 'Missing authorization code. Please try signing in again.';
      setErrorMessage(msg);
      const t = setTimeout(() => navigate('/login', { replace: true }), 2500);
      return () => clearTimeout(t);
    }

    completeSsoLogin(code)
      .then(() => {
        toast({
          title: 'Login Successful',
          description: 'Welcome back!',
          className:
            'bg-green-50 border-green-200 text-green-900 dark:bg-green-900 dark:text-green-100',
        });
        navigate('/', { replace: true });
      })
      .catch(() => {
        const msg = useAuthStore.getState().error || 'Single sign-on failed. Please try again.';
        setErrorMessage(msg);
        toast({ title: 'Sign-in failed', description: msg, variant: 'destructive' });
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      });
  }, [searchParams, completeSsoLogin, navigate, toast]);

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: `url(${clientConfig.loginBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 to-green-900/40" />
      <Card className="w-full max-w-md p-8 space-y-4 bg-white dark:bg-gray-900 shadow-2xl border-border relative z-10 text-center">
        {errorMessage ? (
          <>
            <AlertCircle className="w-8 h-8 text-red-600 dark:text-red-400 mx-auto" />
            <p className="text-sm text-red-700 dark:text-red-300">{errorMessage}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Redirecting to sign in…</p>
          </>
        ) : (
          <>
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-gray-500 dark:text-gray-400" />
            <p className="text-sm text-gray-700 dark:text-gray-300">Completing sign-in…</p>
          </>
        )}
      </Card>
    </div>
  );
}
